/**
 * The currency a page shows. A product's Stripe price is based on the dollar and may carry fixed
 * options in other currencies (`currency_options`); Checkout charges a buyer the option of their own
 * currency when the price has one, and otherwise the dollar base, which Adaptive Pricing converts.
 * The page says what a buyer here will see charged.
 */
export type DisplayCurrency = 'eur' | 'usd' | 'inr'

export const CURRENCY_SYMBOL: Record<DisplayCurrency, string> = {eur: '€', usd: '$', inr: '₹'}

/**
 * Countries whose local currency is one a price can carry as a fixed option. The euro: the twenty
 * eurozone members and the states that use it without membership.
 */
const LOCAL_CURRENCY: ReadonlyMap<string, DisplayCurrency> = new Map([
    ...['AT', 'BE', 'BG', 'HR', 'CY', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PT', 'SK', 'SI', 'ES', // the eurozone
        'AD', 'MC', 'SM', 'VA', 'ME', 'XK', // the euro without membership
    ].map((country) => [country, 'eur'] as const),
    ['IN', 'inr'],
])

/**
 * The currency this request's page shows: the viewer's own when the product's price offers it as a
 * fixed option (`offered`), else the dollar, the base. CloudFront sets `CloudFront-Viewer-Country`
 * (the product's distribution forwards it: `forward_viewer_country`); without it — dev, local — the
 * dollar.
 */
export function displayCurrency(request: Request, offered: readonly DisplayCurrency[] = ['eur']): DisplayCurrency {
    const country = request.headers.get('cloudfront-viewer-country')?.trim().toUpperCase()
    const local = country ? LOCAL_CURRENCY.get(country) : undefined
    return local && offered.includes(local) ? local : 'usd'
}
